/**
 * Credit Types and Constants
 * Credit rules are defined in /docs/credits.md. Do not redefine them.
 * Frontend never deducts credits. Backend is the single source of truth.
 */

// ============================================================================
// CREDIT COST DEFINITIONS (DO NOT CHANGE)
// Exact values from /docs/credits.md
// ============================================================================
export const CREDIT_COSTS = {
  STANDARD_REPORT: 1.5,
  MINI_REPORT: 1,
  GUEST_INVITE: 1,
  SHORT_TERM_PREDICTION: 1, // ≤ 2 years
  FIVE_YEAR_FORECAST: 3,
  TEN_YEAR_FORECAST: 3,
  DATASET_EXPORT: 2,
  HIGH_RES_EXPORT: 1,
  API_BATCH_EXPORT: 1,
} as const;

export type JobType = keyof typeof CREDIT_COSTS;

// ============================================================================
// FREE ACTIONS (DO NOT COST CREDITS)
// These must NEVER cost credits per /docs/credits.md
// ============================================================================
export const FREE_ACTIONS = [
  "VIEW_DASHBOARD",
  "OPEN_PROJECT",
  "EDIT_METADATA",
  "VIEW_SAVED_OUTPUT",
  "RE_EXPORT_EXISTING",
  "BROWSE_HISTORICAL_DATA",
] as const;

export type FreeAction = (typeof FREE_ACTIONS)[number];

// ============================================================================
// CREDIT BALANCE TYPES
// ============================================================================
export interface CreditBalance {
  userId: string;
  creditsBalance: number;
}

export interface CreditLedgerEntry {
  description: string;
  id: string;
  userId: string;
  jobId: string | null;
  creditsDelta: number;
  reason: string;
  createdAt: string;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================
export interface CheckSufficientCreditsRequest {
  userId: string;
  jobType: JobType;
}

export interface CheckSufficientCreditsResponse {
  sufficient: boolean;
  currentBalance: number;
  creditsRequired: number;
  shortfall: number;
}

// Extended response including breakdown of regular and bonus balances
export interface CheckSufficientCreditsResponseWithBreakdown extends CheckSufficientCreditsResponse {
  regular: number;
  bonus: number;
  willUseBase?: boolean;
}

export interface ConsumeCreditsRequest {
  userId: string;
  jobId: string;
  jobType: JobType;
}

export interface ConsumeCreditsResponse {
  success: boolean;
  newBalance?: number;
  creditsDeducted?: number;
  error?: string;
  alreadyProcessed?: boolean;
}

export interface AddCreditsRequest {
  userId: string;
  creditsAmount: number;
  reason: string;
}

export interface AddCreditsResponse {
  success: boolean;
  newBalance?: number;
  creditsAdded?: number;
  error?: string;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get the credit cost for a job type.
 * Credit costs are defined in /docs/credits.md and must not be changed.
 */
export function getCreditCost(jobType: JobType): number {
  return CREDIT_COSTS[jobType];
}

/**
 * Get human-readable description for a job type
 */
export function getJobTypeDescription(jobType: JobType): string {
  const descriptions: Record<JobType, string> = {
    STANDARD_REPORT: "Standard Report",
    MINI_REPORT: "Mini Report",
    GUEST_INVITE: "Guest Invite",
    SHORT_TERM_PREDICTION: "Short-Term Prediction (≤ 2 years)",
    FIVE_YEAR_FORECAST: "5-Year Forecast",
    TEN_YEAR_FORECAST: "10-Year Forecast",
    DATASET_EXPORT: "Dataset Export",
    HIGH_RES_EXPORT: "High-Resolution Export",
    API_BATCH_EXPORT: "API Batch Export",
  };
  return descriptions[jobType];
}

/**
 * Check if an action is free (does not cost credits)
 */
export function isActionFree(action: string): boolean {
  return FREE_ACTIONS.includes(action as FreeAction);
}

/**
 * Format credit amount for display
 */
export function formatCredits(amount: number): string {
  if (amount === 1) {
    return "1 credit";
  }
  return `${amount} credits`;
}

/**
 * Get the appropriate job type for a prediction timeframe
 */
export function getPredictionJobType(timeframe: "2y" | "5y" | "10y"): JobType {
  switch (timeframe) {
    case "2y":
      return "SHORT_TERM_PREDICTION";
    case "5y":
      return "FIVE_YEAR_FORECAST";
    case "10y":
      return "TEN_YEAR_FORECAST";
  }
}

/**
 * Get the appropriate job type for a report
 */
export function getReportJobType(isMultiRegion: boolean): JobType {
  return isMultiRegion ? "GUEST_INVITE" : "STANDARD_REPORT";
}
